<template>
  <master-layout pageTitle="Google RCS Compliance Keywords">
    <ComplianceKeywords 
      ref="complianceKeywords"
      channel="googlercs"
      :initialKeywordsData="rcsKeywords"
      :messageJsonElements="adMessageJsonElements"
      :carouselMessage="rcsCarouselData"
      :selectedTemplate="selectedTemplate"
      :previewData="previewData"
      :templatesLoading="templatesLoading"
      @refresh="handleRefresh"
      @load-keywords="loadRcsKeywords"
      @keyword-added="handleKeywordAdded"
      @fetch-rule="handleFetchRule"
    >
      <template #message-content>
        <div v-if="!fetchingComplianceLoading" class="rcs-message-template">
          <JsonBuilderWrapper 
            v-if="selectedRule"
            ref="rcsJsonBuilderWrapper"
            :editBroadcast="formattedEditBroadcast"
            :uploaded_url_type="selectedRule?.url_types?.HEADER?.[0] || 'static'"
            :isViewOnlyMode="false"
            :isEditMode="!!selectedRule.id"
            :enableURLUpload="false"
            :hideMessageTypeSelector="false"
            :isSmsCompliance="false"
            :isGoogleRcsCompliance="true"
            @finalJson="handleFinalJson"
            @finalJsonElements="handleFinalJsonElements"
            @loading="handleJsonBuilderLoading"
            @carouselData="handleCarouselData"
          />
          <div v-else class="no-rule-selected">
            <p class="vtext-subtitle">Select a compliance keyword rule to configure RCS message</p>
          </div>
        </div>
      </template>
    </ComplianceKeywords>
  </master-layout>
</template>

<script>
import ComplianceKeywords from '@/components/ComplianceKeywords.vue';
import JsonBuilderWrapper from '@/pages/broadcast/json-builder-wrapper.vue';
import apiInterceptor from '@/shared/apiInterceptor';
import moment from 'moment-timezone';
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env';
import { queryClient } from '@/shared/queryClient';
import { complianceKeywordsByChannelQueryOptions } from '@/composables/useComplianceKeywords';
import { validateRcsUrl, findInvalidRcsButtonUrl } from '@/helpers/rcs-url.helpers.js';

export default {
  name: 'GoogleRcsComplianceKeywords',
  components: { 
    ComplianceKeywords,
    JsonBuilderWrapper
  },
  data() {
    return {
      rcsKeywords: [],
      templatesLoading: false,
      selectedRule: {},
      selectedTemplate: {},
      previewData: {},
      jsonBuilderData: {
        allElements: [],
        finalJson: {}
      },
      adMessageJsonElements: [],
      rcsCarouselData: [],
      fetchingComplianceLoading: false
    }
  },
  mounted() {
    // Initialize clean state on mount
    this.adMessageJsonElements = [];
    this.rcsCarouselData = [];
  },
  async ionViewWillEnter() {
    // Force-invalidate so fetchQuery always hits the network on page entry.
    // Compliance data can be changed by any user of the shop at any time.
    queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('googlercs').queryKey });
    await this.loadRcsKeywords();
  },
  async beforeUnmount() {
    // Ensure compliance keywords component loading is cleared
    if (this.$refs.complianceKeywords) {
      this.$refs.complianceKeywords.setLoading(false);
    }
  },
  computed: {
    formattedEditBroadcast() {
      if (!this.selectedRule || Object.keys(this.selectedRule).length === 0) {
        return {
          channel: 'googlercs'
        };
      }
      const editBroadcast = {
        id: this.selectedRule.id,
        channel: 'googlercs',
        message: this.selectedRule.message || {},
        url_types: this.selectedRule.url_types || {},
        answers: []
      };
      // Convert compliance message to answers format that JSON builder expects
      if (this.selectedRule.message && !this.isMessageEmpty(this.selectedRule.message)) {
        editBroadcast.answers.push(this.selectedRule.message);
      }      
      return editBroadcast;
    }
  },
  methods: {
    moment,
    async handleRefresh() {
      queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('googlercs').queryKey });
      await this.loadRcsKeywords();
    },
    async loadRcsKeywords() {
      try {
        // fetchQuery respects stale state — re-fetches after invalidateQueries, otherwise serves from
        // the ['compliance-keywords', 'googlercs'] cache. googlercs mutations only invalidate this key.
        const rcsCompliance = await queryClient.fetchQuery(complianceKeywordsByChannelQueryOptions('googlercs'));
        if (Array.isArray(rcsCompliance)) {
          // Transform API response to component format
          this.rcsKeywords = rcsCompliance.map(item => ({
            id: item.id,
            keywords: item.keywords || [],
            type: item.type === 'opt_in' ? 'Opt-in' : 'Opt-out',
            description: item.description,
            active: item.active,
            channel: item.channel,
            seller_id: item.seller_id,
            message: item.message,
            url_types: item.url_types,
            template_data: item.template_data
          }));
          // Update the ComplianceKeywords component with the loaded data
          this.$refs.complianceKeywords?.updateKeywordsData(this.rcsKeywords);
        } else {
          this.rcsKeywords = [];
          this.$refs.complianceKeywords?.updateKeywordsData([]);
        }
      } catch (error) {
        this.rcsKeywords = [];
        useUIStore().updateToast({ open: true, message: error.message || "Failed to load Google RCS compliance keywords", type: "danger" })
      }
    },
    
    async handleKeywordAdded(data) {
      try {
        this.$refs.complianceKeywords?.setLoading(true);
        
        // Message content is now optional for RCS
        let messageData = { message: {}, url_types: {} };
        if (this.adMessageJsonElements && this.adMessageJsonElements.length > 0) {
          // Validate message configuration only if message content exists
          if (!this.validateJsonBuilderConfiguration()) {
            this.$refs.complianceKeywords?.setLoading(false);
            return;
          }
          // Get message data from JSON builder
          messageData = this.getSelectedRuleMessageData();
        }
        
        let reqBody = {
          keywords: data.keywords,
          type: data.type, // opt_in or opt_out
          description: data.description || "",
          active: data.active,
          channel: data.channel,
          message: messageData.message || {},
          url_types: messageData.url_types || {},
        };
        // Check if we're editing existing keywords (has ID) or creating new ones
        if (data.id) {
          // updating existing compliance keywords
          await apiInterceptor.put( environment("VUE_APP_API_URL") + `social/v1/compliance/${data.id}`, reqBody );
          useUIStore().updateToast({ 
            open: true, 
            message: "Compliance keywords updated successfully", 
            type: "success" 
          });
        } else {
          //  creating new compliance keywords
          await apiInterceptor.post( environment("VUE_APP_API_URL") + "social/v1/compliance", reqBody );
          useUIStore().updateToast({ 
            open: true, 
            message: "Compliance keywords created successfully", 
            type: "success" 
          });
        }
        // Invalidate only the googlercs-scoped cache key, then reload.
        queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('googlercs').queryKey });
        await this.loadRcsKeywords();
        this.selectedRule = {};
        this.selectedTemplate = {};
        this.previewData = {};
        this.jsonBuilderData = { allElements: [], finalJson: {} };
        this.adMessageJsonElements = [];
        this.rcsCarouselData = [];
        
        this.$refs.complianceKeywords?.resetToKeywordsTable(); // Navigate back to keywords table
      } catch (error) {
        useUIStore().updateToast({ 
          open: true, 
          message: error.message || "Failed to save compliance keywords", 
          type: "danger" 
        });
      } finally {
        this.$refs.complianceKeywords?.setLoading(false);
      }
    },

    handleFinalJson(jsonData) {
      this.jsonBuilderData.finalJson = jsonData;
      
      // Update the selected rule's template data
      if (this.$refs.complianceKeywords?.selectedRule) {
        this.$refs.complianceKeywords.selectedRule.template_data = {
          ...this.$refs.complianceKeywords.selectedRule.template_data,
          message: jsonData.message || {},
          url_types: jsonData.url_types || {}
        };
      }
      
      // Also update our local selectedRule
      if (this.selectedRule) {
        this.selectedRule.template_data = {
          ...this.selectedRule.template_data,
          message: jsonData.message || {},
          url_types: jsonData.url_types || {}
        };
      }
    },

    handleFinalJsonElements(elementsData) {   
      // Handle the data structure from JSON builder
      if (elementsData && elementsData.finalJsonElements) {
        this.adMessageJsonElements = elementsData.finalJsonElements;
        this.jsonBuilderData.allElements = elementsData.finalJsonElements;
      } else if (Array.isArray(elementsData)) {
        this.adMessageJsonElements = elementsData;
        this.jsonBuilderData.allElements = elementsData;
      }      
      // Update template data with elements
      if (this.$refs.complianceKeywords?.selectedRule) {
        this.$refs.complianceKeywords.selectedRule.template_data = {
          ...this.$refs.complianceKeywords.selectedRule.template_data,
          allElements: this.jsonBuilderData.allElements,
          adMessageJsonElements: this.adMessageJsonElements
        };
      }
      
      // Also update our local selectedRule
      if (this.selectedRule) {
        this.selectedRule.template_data = {
          ...this.selectedRule.template_data,
          allElements: this.jsonBuilderData.allElements,
          adMessageJsonElements: this.adMessageJsonElements
        };
      }
    },

    handleCarouselData(carouselData) {
      this.rcsCarouselData = carouselData;
      
      // Update template data with carousel
      if (this.$refs.complianceKeywords?.selectedRule) {
        this.$refs.complianceKeywords.selectedRule.template_data = {
          ...this.$refs.complianceKeywords.selectedRule.template_data,
          rcsCarouselData: carouselData
        };
      }
      
      // Also update our local selectedRule
      if (this.selectedRule) {
        this.selectedRule.template_data = {
          ...this.selectedRule.template_data,
          rcsCarouselData: carouselData
        };
      }
    },

    handleJsonBuilderLoading(isLoading) {      
      // Only handle JSON builder specific loading, don't interfere with main compliance loading
    },


    clearAllLoadingStates() {
      // Clear compliance keywords loading
      if (this.$refs.complianceKeywords) {
        try {
          this.$refs.complianceKeywords.setLoading(false);
        } catch (error) {
          console.warn('Error clearing compliance keywords loading:', error);
        }
      }      
    },
    // Convert JSON builder elements to message format
    getAdMessageJson() {
      const adMessageElement = this.adMessageJsonElements[0] || {};
      const { type, payload, buttons, title, subtitle, fileName, contentType, fileExtension } = adMessageElement;
      let adMessageJson = {};
      switch (type) {
        case 'text':
          if (buttons?.length) {
            let buttonsToUse = [];
            buttons.forEach((elm) => {
              let elmButton = {
                title: elm.text,
                description: elm.text,
                type: elm.type,
              }
              if (elm.payload) {
                elmButton.payload = elm.payload;
              } else if(elm.url) {
                elmButton.url = elm.url;
                elmButton.height_ratio = "full";
              } else {
                elmButton.url = elm.url;
                elmButton.height_ratio = "full";
              }
              buttonsToUse.push(elmButton);
            });

            adMessageJson = [
              {
                message: [payload],
                template_type: "buttonreply",
                template: { buttons: buttonsToUse }
              }
            ]
          } else {
            // Remove only leading and trailing newlines, not those in the middle
            const cleanedPayload = this.cleanPayload(payload);
            adMessageJson = [{
              message: [cleanedPayload],
              template_type: "normalchat"
            }]
          }
          break;
        case 'image':
          adMessageJson = [{
            message: [payload || ""],
            template_type: "cancelled"
          }]
          break;
        case 'video':
          adMessageJson = [
            {
              message: [ payload || "" ],
              template_type: "videoreply",
              mediatype: "video"
            }
          ];
          break;
        case 'document':
          adMessageJson = [
            {
              message: [ payload || "" ],
              template_type: "document",
              mediatype: "document",
              documentname: fileName,
              documenturl: payload,
              contenttype: contentType,
              extension: fileExtension,
            }
          ];
          break;
        case 'carousel':
        case 'richcard': {
          adMessageJson = this.rcsCarouselData;
          for (const item of adMessageJson) {
            // Handle each element inside the carousel
            if (item.template?.elements) {
              for (const element of item.template.elements) {
                // Thumbnail alignment based on card_orientation
                if (element?.extrainfo?.card_orientation === "VERTICAL") {
                  delete element.extrainfo.thumbnail_alignment;
                } else if (element?.extrainfo?.card_orientation === "HORIZONTAL") {
                  delete element.extrainfo.height;
                }

                // Handle calendar formatting inside element.buttons
                if (element.buttons) {
                  for (const button of element.buttons) {
                    if (button.type === "calendar" && button.payload) {
                      const result = this.formatDateTimeToISOString(button.payload);
                      if (result && result.error) {
                        console.error("Payload formatting failed:", result.error);
                        return;
                      } else {
                        button.payload = result;
                      }
                    }
                  }
                }

              }
            }
            if (item.template?.buttons) {
              for (const button of item.template.buttons) {
                if (button.text) {
                  button.title = button.text;
                  delete button.text;
                }
                if (button.type === "calendar" && button.payload) {
                  const result = this.formatDateTimeToISOString(button.payload);
                  if (result && result.error) {
                    console.error("Payload formatting failed:", result.error);
                    return;
                  } else {
                    button.payload = result;
                  }
                }
              }
            }
          }
          break;
        }
        case 'reply': {
          const buttonsToUse = [];
          for (const elm of buttons) {
            const { text, payload, extrainfo, type, url } = elm;

            const buttonObj = {
              title: text,
              description: text,
            };

            if (type) {
              buttonObj.type = type;
              if (type === 'calendar') {
                const result = this.formatDateTimeToISOString(payload);
                if (result && result.error) {
                  console.error("Payload formatting failed:", result.error);
                  return;
                } else {
                  buttonObj.payload = result;
                }
              } else if (type === 'postback' && payload) {
                buttonObj.payload = payload;
              } else if ((type === 'url' || type == 'webview') && url) {
                buttonObj.url = url;
                buttonObj.height_ratio = "full";
              } else if (payload) {
                buttonObj.payload = payload;
              }
            } else if (payload) {
              buttonObj.payload = payload;
            }

            if (extrainfo) {
              buttonObj.extrainfo = extrainfo;
            }

            buttonsToUse.push(buttonObj);
          }

          adMessageJson = [
            {
              message: [payload],
              template_type: "quickreplies",
              template: { buttons: buttonsToUse }
            }
          ];
          break;
        }
        default:
          console.warn('Unknown message type:', type);
          adMessageJson = [{
            message: [payload || ""],
            template_type: "normalchat"
          }];
      }

      console.log('Generated ad message JSON:', adMessageJson);
      return adMessageJson;
    },
    getSelectedRuleMessageData() {
      // convert JSON builder elements to message format
      if (this.adMessageJsonElements && this.adMessageJsonElements.length > 0) {
        const adMessageJson = this.getAdMessageJson(false);
        
        // Convert array to object format for compliance keywords API
        const messageObject = Array.isArray(adMessageJson) && adMessageJson.length > 0 
          ? adMessageJson[0] 
          : adMessageJson;
        
        const messageData = {
          message: messageObject,
          url_types: this.jsonBuilderData.finalJson.url_types || {},
        };        
        return messageData;
      }
      
      // Fallback to existing rule data
      if (this.selectedRule && this.selectedRule.message) {
        const fallbackData = {
          message: this.selectedRule.message,
          url_types: this.selectedRule.url_types || {},
          template_data: this.selectedRule.template_data || {}
        };
        console.log('Returning fallback rule data:', fallbackData);
        return fallbackData;
      }      
      return {
        message: {},
        url_types: {},
        template_data: {}
      };
    },
    validateJsonBuilderConfiguration() {
      if (!this.selectedRule) {
        useUIStore().updateToast({ 
          open: true, 
          message: "Please select a compliance rule first", 
          type: "danger" 
        });
        return false;
      }

      // If no message elements, it's still valid (optional message)
      if (!this.adMessageJsonElements || this.adMessageJsonElements.length === 0) {
        return true;
      }

      if (!this.$refs.rcsJsonBuilderWrapper) {
        // Builder is not mounted — user is on the keywords accordion, not the message
        // accordion. adMessageJsonElements was populated from a previous visit to the
        // message accordion or from the existing rule data, so it is already valid.
        // Use the existing rule data as-is and allow the save to proceed.
        return true;
      }

      return this.validateRCSMessageTemplate();
    },

    // Google RCS compliance validation
    validateRCSMessageTemplate() {
      const $refJsonBuilderWrapper = this.$refs.rcsJsonBuilderWrapper;
      const adMessageJsonElements = this.adMessageJsonElements;

      if (!$refJsonBuilderWrapper?.messageType) {
        useUIStore().updateToast({ 
          open: true, 
          message: "Please select a message type.", 
          type: "danger" 
        });
        return false;
      }

      // Get the actual message JSON to validate
      const messages = this.getAdMessageJson();
      if (messages === undefined) {
        return false;
      } else {
        // Google RCS specific validations for carousel/richcard
        if (messages[0].template_type == 'carousel' || messages[0].template_type == 'quickcarousel' || messages[0].template_type == 'richcard') {
          const elements = messages?.[0]?.template?.elements;
          const quickReplyButtons = messages?.[0]?.template?.buttons;
          
          if (elements) {
            for (let i = 0; i < elements.length; i++) {
              const element = elements[i];
              if (!element.title?.trim()) {
                useUIStore().updateToast({ 
                  open: true, 
                  message: `Please enter a title for carousel ${i + 1} and try again!`, 
                  type: "danger" 
                });
                return false;
              }
              if (!element.subtitle?.trim()) {
                useUIStore().updateToast({ 
                  open: true, 
                  message: `Please enter a description for carousel ${i + 1} and try again!`, 
                  type: "danger" 
                });
                return false;
              }
              if (element.buttons) {
                for (const button of element.buttons) {
                  const validationError = this.validateCalendarButton(button);
                  if (validationError) {
                    useUIStore().updateToast({ 
                      open: true, 
                      message: validationError, 
                      type: "danger" 
                    });
                    return false;
                  }
                }
              }
            }
          }
          
          if (quickReplyButtons) { // calendar validations for Quick reply
            for (const button of quickReplyButtons) {
              const validationError = this.validateCalendarButton(button);
              if (validationError) {
                useUIStore().updateToast({ 
                  open: true, 
                  message: validationError, 
                  type: "danger" 
                });
                return false;
              }
            }
          }
        }

        const defaultUrlType = this.selectedRule?.url_types?.HEADER?.[0] || 'static';
        const invalidRcsButtonUrl = findInvalidRcsButtonUrl(messages, defaultUrlType);
        if (invalidRcsButtonUrl) {
          useUIStore().updateToast({
            open: true,
            message: "Please enter valid RFC 3986 URL(s) for all RCS URL buttons and try again!",
            type: "danger"
          });
          return false;
        }
        
        // Validate message content
        if (!Array.isArray(messages) || messages.length === 0 || !messages[0]?.message || !Array.isArray(messages[0].message)) {
          useUIStore().updateToast({ 
            open: true, 
            message: "Please create message inside create message section!", 
            type: "danger" 
          });
          return false;
        }
        
        const messageContent = messages[0].message[0];
        const templateType = messages[0].template_type;
        
        // Check for empty message
        if ([undefined, null].includes(messageContent)) {
          useUIStore().updateToast({ 
            open: true, 
            message: "Please enter message inside create message section!", 
            type: "danger" 
          });
          return false;
        }

        // Validate media URL-based templates
        const mediaTemplates = ["cancelled", "videoreply", "document"];
        if (mediaTemplates.includes(templateType)) {
          const selectedUrlType = this.selectedRule?.url_types?.HEADER?.[0] || 'static';
          const isValidUrl = validateRcsUrl(messageContent, selectedUrlType === 'dynamic');
          if (!isValidUrl) {
            let errorMessage = "Please provide a valid media and try again!";
            if (templateType === "cancelled") {
              errorMessage = "Please provide a valid image and try again!";
            } else if (templateType === "videoreply") {
              errorMessage = "Please provide a valid video and try again!";
            } else if (templateType === "document") {
              errorMessage = "Please provide a valid document and try again!";
            }
            useUIStore().updateToast({ 
              open: true, 
              message: errorMessage, 
              type: "danger" 
            });
            return false;
          }
        }

        // Handle text/quick reply templates
        const textBasedTemplates = ["quickreplies", "normalchat", "reply", "buttonreply"];
        if (textBasedTemplates.includes(templateType) && typeof messageContent === "string" && messageContent.trim() === "") {
          useUIStore().updateToast({ 
            open: true, 
            message: "Please enter message inside create message section!", 
            type: "danger" 
          });
          return false;
        }

        if (!adMessageJsonElements?.[0]) {
          useUIStore().updateToast({ 
            open: true, 
            message: "Please create message inside create message section!", 
            type: "danger" 
          });
          return false;
        }
      }

      return true; // No errors
    },

    formatPayloadDateRange(payload) {
      if (!payload) return '';

      const [start, end] = payload.split(' ');

      if (!start || !end) return '';

      const startFormatted = moment(start).format('MM/DD/YYYY, HH:mm:ss');
      const endFormatted = moment(end).format('MM/DD/YYYY, HH:mm:ss');

      return `${startFormatted}, ${endFormatted}`;
    },

    validateCalendarButton(button) {
      if (button?.type === 'calendar' && button?.payload) {
        const alternativePayloadFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z \d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

        // Normalize the payload if in alternative ISO format
        if (alternativePayloadFormat.test(button.payload)) {
          button.payload = this.formatPayloadDateRange(button.payload);
        }
        // Parse formatted payload
        const payloadParts = button.payload.split(',');
        const format = "MM/DD/YYYY, HH:mm:ss";
        const startDateAndTimeStr = payloadParts[0]?.trim() + ', ' + payloadParts[1]?.trim();
        const endDateAndTimeStr = payloadParts[2]?.trim() + ', ' + payloadParts[3]?.trim();

        const start = moment(startDateAndTimeStr, format, true);
        const end = moment(endDateAndTimeStr, format, true);
        const now = moment();

        // Check if start or end is before current time
        if (start.isBefore(now)) {
          return `Please enter a valid start date & time for button "${button.title}".`;
        }

        if (end.isBefore(now)) {
          return `Please enter a valid end date & time for button "${button.title}".`;
        }

        // Check if end is before start
        if (end.isBefore(start)) {
          return `End date & time cannot be earlier than start date & time for button "${button.title}".`;
        }
      }
    },

    async handleFetchRule(ruleId) {
      try {
        if (ruleId) {
          this.fetchingComplianceLoading = true;
          const response = await apiInterceptor.get( environment("VUE_APP_API_URL") + `social/v1/compliance/${ruleId}` );
          
          if (response) {
            const ruleData = {
              id: response.id,
              keywords: response.keywords || [],
              type: response.type === 'opt_in' ? 'Opt-in' : 'Opt-out',
              description: response.description,
              active: response.active,
              channel: response.channel,
              seller_id: response.seller_id,
              message: response.message,
              url_types: response.url_types,
              template_data: response.template_data
            };
            
            // Update the selected rule in the component
            this.$refs.complianceKeywords?.updateSelectedRule(ruleData);
            
            // Set the rule data for JSON builder
            this.selectedRule = ruleData;
            
            // Clear previous data
            this.selectedTemplate = {};
            this.previewData = {};
            this.jsonBuilderData = { allElements: [], finalJson: {} };
            this.adMessageJsonElements = [];
            this.rcsCarouselData = [];
            
            // Check if rule has empty message and set "none" in dropdown
            this.$nextTick(() => {
              if (this.$refs.rcsJsonBuilderWrapper) {
                const hasEmptyMessage = this.isMessageEmpty(ruleData.message);
                
                if (hasEmptyMessage) {
                  console.log('Rule has empty message, setting dropdown to "none"');
                  // Set the dropdown to "none" for empty messages
                  this.$refs.rcsJsonBuilderWrapper.setMessageTypeToNone();
                } else {
                  console.log('Rule has message content, keeping existing behavior');
                }
              }
            });
          } else {
            throw new Error('Rule not found');
          }
        }
      } catch (error) {
        useUIStore().updateToast({ 
          open: true, 
          message: error.message || "Failed to load compliance rule data", 
          type: "danger" 
        });
        this.$refs.complianceKeywords?.setLoading(false);
      } finally {
        this.fetchingComplianceLoading = false;
      }
    },
    isMessageEmpty(message) {
      // Check various conditions for empty messages
      if(message?.template?.elements?.length) return false;
      if (!message) return true;
      if (typeof message === 'object' && Object.keys(message).length === 0) return true;
      if (Array.isArray(message?.message) && message.message.length === 0) return true;
      if (typeof message?.message === 'string' && message.message.trim() === '') return true;
      
      // Check if message array contains only empty/null values
      if (Array.isArray(message?.message)) {
        const hasContent = message.message.some(item => 
          item && (typeof item === 'string' ? item.trim() !== '' : true)
        );
        if (!hasContent) return true;
      }
      
      return false;
    },
    cleanPayload(payload) {
      if (typeof payload !== 'string') return payload;
      // Remove leading/trailing newlines and spaces, but keep newlines in the middle
      return payload.replace(/^[\s\n]+|[\s\n]+$/g, '');
    },
    formatDateTimeToISOString(payload) {
			const format = "MM/DD/YYYY, HH:mm:ss";
		
			let parts = payload.split(",");

			if (parts.length < 4) {
        payload = this.formatPayloadDateRange(payload);
        parts = payload.split(",");
			}
			const startStr = `${parts[0].trim()},${parts[1].trim()}`;
			const endStr = `${parts[2].trim()},${parts[3].trim()}`;
		
			const start = moment(startStr, format);
			const end = moment(endStr, format);
      const now = moment();
		
			if (!start.isValid() || !end.isValid()) {
				useUIStore().updateToast({
					open: true,
					message: "Invalid date format. Please check the inputs.",
					type: "danger",
				});
        return { error: "Invalid date format. Please check the inputs." };
			}
      if (start.isBefore(now)) {
        useUIStore().updateToast({
          open: true,
          message: "Start date and time cannot be in the past.",
          type: "danger",
        });
        return { error: "Start date and time cannot be in the past." };
      }

			if (end.isBefore(start)) {
				useUIStore().updateToast({
					open: true,
					message: "End date cannot be earlier than start date.",
					type: "danger",
				});
        return { error: "Start date and time cannot be in the past." };
			}
		
			return `${start.toISOString(true)} ${end.toISOString(true)}`;
		},
  }
}
</script>

<style scoped>
.rcs-message-template {
  padding: 16px 0;
}

.no-rule-selected {
  padding: 20px;
  text-align: center;
  background: var(--background-primary);
  border-radius: 8px;
  margin: 8px 0;
}
</style>